import { useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage } from '../../api/client';
import { useAuth } from './AuthContext';

/**
 * Auth modal — openAuth() equivalent.
 *
 * The prototype's requireAuth() (docs/index.html:751) stores the pending
 * action in state.afterAuth, opens this, and replays the action on success.
 * `onSuccess` is that replay hook.
 */
export function AuthModal({
  mode,
  onClose,
  onSuccess,
}: {
  mode: 'login' | 'signup';
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'login' | 'signup'>(mode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = tab === 'login'
        ? await login(email, password)
        : await signup(name, email, password);
      onSuccess?.();
      onClose();
      if (!onSuccess && user.role !== 'student') navigate('/admin');
    } catch (err) {
      // Surface the server's real message (e.g. the password policy) rather
      // than a generic "failed" — the API already returns a usable envelope.
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-back open" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={tab === 'login' ? 'Log in' : 'Sign up'}>
        <div className="auth-tabs">
          <button
            className={`auth-tab ${tab === 'login' ? 'on' : ''}`}
            data-testid="auth-tab-login"
            onClick={() => setTab('login')}
          >
            Log in
          </button>
          <button
            className={`auth-tab ${tab === 'signup' ? 'on' : ''}`}
            data-testid="auth-tab-signup"
            onClick={() => setTab('signup')}
          >
            Sign up free
          </button>
        </div>

        <form onSubmit={(e) => void submit(e)}>
          {tab === 'signup' ? (
            <div className="field">
              <label htmlFor="authName">Your name</label>
              <input
                id="authName"
                data-testid="auth-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
              />
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="authEmail">Email</label>
            <input
              id="authEmail"
              data-testid="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="authPassword">Password</label>
            <input
              id="authPassword"
              data-testid="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {tab === 'signup' ? (
              <span className="asub" style={{ fontSize: 12 }}>
                At least 8 characters, with a letter and a number.
              </span>
            ) : null}
          </div>

          {error ? (
            <p data-testid="auth-error" style={{ color: 'var(--coral)', fontSize: 13.5, margin: '6px 0' }}>
              {error}
            </p>
          ) : null}

          <button className="btn btn-primary btn-block" type="submit" data-testid="auth-submit" disabled={busy}>
            {busy ? 'Please wait…' : tab === 'login' ? 'Log in' : 'Create free account'}
          </button>
        </form>

        <button className="btn btn-ghost btn-block btn-sm" onClick={onClose} data-testid="auth-close">
          Cancel
        </button>
      </div>
    </div>
  );
}
